import { useMemo, type ReactNode } from 'react';
import type { Navigation } from '../../types/navigation';
import { useLocation } from 'react-router-dom';
import { AppShell } from '@locus-taxy/disco-ui';
import { useTranslation } from '@locus-taxy/encore-i18n-toolkit';

import { DashboardLink } from '../DashboardLink';
import { resolveActiveHref, toDiscoNav } from '../../utils/toDiscoNav';
import DiscoSidebarFooter from './DiscoSidebarFooter';
import RecordAccessSelector from './RecordAccessSelector';

type DiscoAppShellProps = Readonly<{
  navigation: Navigation;
  bottomNavigation?: Navigation;
  children: ReactNode;
}>;

type SlotArgs = { collapsed: boolean };

// Router adapter for AppShell's nav leaves. AppShell renders the leaf via
// `<SidebarMenuSubButton asChild …><LinkComponent/></SidebarMenuSubButton>`,
// and the Radix Slot merges the button's `data-active`/`data-slot`/`data-size`
// props onto this child. The active-row highlight is driven by
// `data-[active=true]:bg-sidebar-accent`, so the adapter MUST spread those
// merged props through to the link — destructuring only `to/children/className`
// (as the published LinkComponent type suggests) drops `data-active` and the
// row never highlights. So we pass `...rest` through.
//
// `<DashboardLink>` rather than a bare `<Link>` (DP-3188 AC8): every top-level
// module switch happens through this one leaf, so the merge here is what keeps
// the session-scoping query alive across the sidebar.
const RouterLink: React.ComponentType<{
  to: string;
  children: ReactNode;
  className?: string;
}> = ({ to, children, ...rest }) => (
  <DashboardLink to={to} {...rest}>
    {children}
  </DashboardLink>
);

function renderSidebarHeader({ collapsed }: SlotArgs) {
  return <RecordAccessSelector collapsed={collapsed} />;
}

function renderSidebarFooter({ collapsed }: SlotArgs) {
  return <DiscoSidebarFooter collapsed={collapsed} />;
}

export default function DiscoAppShell({
  navigation,
  bottomNavigation = [],
  children,
}: DiscoAppShellProps) {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  // Detail routes (`.../entities/foo/<id>`) aren't nav leaves themselves;
  // collapse them onto their owning list leaf so the active highlight and the
  // group's open-state both resolve via AppShell's exact `pathname === href`.
  // Resolve against both roots so a route owned by a bottom-nav leaf still
  // highlights (and pre-opens its group) in the sidebar footer.
  const activeHref = useMemo(
    () => resolveActiveHref([...navigation, ...bottomNavigation], pathname),
    [navigation, bottomNavigation, pathname]
  );
  const nav = useMemo(
    () => toDiscoNav(navigation, activeHref, t),
    [navigation, activeHref, t]
  );
  // `wrapOrphans: false` — the footer's root-level leaves (Settings and its
  // siblings) render beside each other, not inside a synthetic group.
  const bottomNav = useMemo(
    () => toDiscoNav(bottomNavigation, activeHref, t, false),
    [bottomNavigation, activeHref, t]
  );

  return (
    <AppShell
      nav={nav}
      bottomNav={bottomNav}
      darkSidebar
      insetLayout
      defaultSidebarOpen
      insetClassName="overflow-hidden"
      // Omit `brand` so AppShell uses its DEFAULT_BRAND — the real Locus
      // logomark + wordmark — instead of a placeholder. (Previously a hand-
      // rolled "L" box.)
      // Forward the reactive react-router pathname (not AppShell's
      // window.location fallback, which is captured once at mount during the
      // auth redirect and never updates) so the active nav leaf highlights and
      // tracks client-side navigation. RouterLink keeps nav clicks client-side.
      pathname={activeHref}
      LinkComponent={RouterLink}
      sidebarHeaderSlot={renderSidebarHeader}
      sidebarFooterSlot={renderSidebarFooter}
    >
      {children}
    </AppShell>
  );
}
